import { ScalePlanParameters } from "../../../utils/commonTypes";
import { VuiFlexContainer, VuiNumberInput, VuiSpinner, VuiText, VuiTextColor, VuiToggle } from "@vectara/vectara-ui";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useCustomerContext } from "../../../utils/CustomerContext";
import { readCustomerEnvironment } from "../../../api/api";
import { useLoginContext } from "../../../utils/LoginContext";
import { BytesToMebiBytes, MebiBytesToBytes } from "../../../utils/commonFunctions";

type Props = {
  parameters: ScalePlanParameters;
  setParameters: (parameters: ScalePlanParameters) => void;
};

export const ScaleParameters = ({ parameters, setParameters }: Props) => {
  const { customer } = useCustomerContext();
  const { authToken } = useLoginContext();

  useEffect(() => {
    const setDefaultParameters = async () => {
      const customerEnvironment = await readCustomerEnvironment(authToken, customer.customerId);
      setParameters({ effectiveFromEpochSecond: dayjs().unix(), tableExtractionEnabled: customer.tableExtractionEnabled, maxBytes: customerEnvironment.maxBytes });
    }
    setDefaultParameters();
  }, [])

  const handlePlanEffectiveFromChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    setParameters({ ...parameters, effectiveFromEpochSecond: date.unix() });
  }

  const handleTableExtractionToggle = () => {
    setParameters({ ...parameters, tableExtractionEnabled: !parameters.tableExtractionEnabled });
  }

  const handleMaxBytesChange = (value: number | undefined) => {
    if(!value) return;
    setParameters({ ...parameters, maxBytes: MebiBytesToBytes(value) });
  }

  if(!parameters) {
    return <VuiSpinner />
  }

  return (
    <VuiFlexContainer direction="column" spacing="xl">
      <VuiFlexContainer direction="column" spacing="xs">
        <VuiText>
          <h3>Plan Effective From</h3>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={["year", "month", "day"]}
              value={parameters.effectiveFromEpochSecond ? dayjs.unix(parameters.effectiveFromEpochSecond) : dayjs()}
              onChange={handlePlanEffectiveFromChange}
            />
          </LocalizationProvider>
        </VuiText>
      </VuiFlexContainer>
      <VuiFlexContainer direction="column" spacing="xs">
        <VuiText>
          <h3>Table Extraction Enabled</h3>
          <VuiText className="switch-description">
            <VuiTextColor color="accent">
              Note: Toggle is set to current value by default.
            </VuiTextColor>
          </VuiText>
        </VuiText>
        <VuiToggle checked={parameters.tableExtractionEnabled} onChange={handleTableExtractionToggle} />
      </VuiFlexContainer>
      <VuiFlexContainer direction="column" spacing="xs">
        <VuiText><h3>Max Storage (MB)</h3>
          <VuiText>
            <VuiTextColor color="accent">
              Sets the max storage in the environment. By default, it is set to current value.
            </VuiTextColor>
          </VuiText>
        </VuiText>
        <VuiFlexContainer><VuiNumberInput value={BytesToMebiBytes(parameters.maxBytes)} onChange={handleMaxBytesChange}/></VuiFlexContainer>
      </VuiFlexContainer>
    </VuiFlexContainer>
  );
};
